/**
 * The analyst's session classifications (`SessionClassifications` on the case).
 *
 * A value is one of:
 *   'safe'                                 — the analyst judged the session the user's own (v1.53.2)
 *   { verdict: 'attacker', reason, by, at } — the analyst judged the session STOLEN, from the
 *                                            sign-in that created it (v1.190.0)
 *
 * 🔴 WHY A SESSION MARK EXISTS. A session becomes "compromised" when a malicious address touches
 * it, and its compromise start is that FIRST TOUCH; everything on it before then is treated as
 * the user's own prior use and hidden by "Pre-compromise hidden". When an attacker relay CREATES
 * the session (AiTM), the rows before the first malicious touch are not the user — they are the
 * capture: the sign-in, the MFA approval, the keep-me-signed-in token. Classifying the relay's
 * address malicious would surface them, but the relay is often a residential proxy or the
 * victim's own address. The fact is about the session, so the mark is on the session: its
 * compromise start becomes its FIRST row, and no address is classified.
 *
 * `by`/`at` are stamped by the server (Merge-CaseSessionClassifications); a reason is required.
 */

/** Parse the case field (JSON string or object) into a plain { sid: value } object. */
export function parseSessionClassifications(value) {
  let obj = value;
  if (typeof obj === 'string') {
    try { obj = JSON.parse(obj || '{}'); } catch { obj = {}; }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out = {};
  for (const [sid, v] of Object.entries(obj)) {
    if (!sid) continue;
    if (v === 'safe') out[sid] = 'safe';
    else if (v && typeof v === 'object' && v.verdict === 'attacker' && String(v.reason || '').trim()) {
      out[sid] = { verdict: 'attacker', reason: String(v.reason).trim(), by: String(v.by || ''), at: String(v.at || '') };
    }
  }
  return out;
}

/** 'safe' | 'attacker' | 'default' for one session. */
export function sessionStatusOf(classifications, sessionId) {
  const v = classifications && sessionId ? classifications[sessionId] : null;
  if (v === 'safe') return 'safe';
  if (v && typeof v === 'object' && v.verdict === 'attacker') return 'attacker';
  return 'default';
}

/** Map<sessionId, { reason, by, at }> of the sessions the analyst marked as the attacker's. */
export function attackerSessionMarks(classifications) {
  const out = new Map();
  for (const [sid, v] of Object.entries(classifications || {})) {
    if (v && typeof v === 'object' && v.verdict === 'attacker' && String(v.reason || '').trim()) {
      out.set(sid, { reason: String(v.reason).trim(), by: String(v.by || ''), at: String(v.at || '') });
    }
  }
  return out;
}

/**
 * Collections whose rows carry a session id (`SessionId` on sign-ins, `AADSessionId` on
 * Exchange/SharePoint). Every collection a marked session's rows can live in — the
 * non-interactive feed included, because that is where a stolen session is replayed.
 */
export const SESSION_MARK_SOURCES = [
  'SignInEvents', 'NonInteractiveSignInEvents', 'ExchangeEvents', 'SharePointEvents',
  'MessageTraceEvents', 'UrlClickEvents', 'GeneralEvents', 'DlpEvents',
  'AzureActivityEvents', 'RiskDetectionEvents', 'SecurityAlertEvents',
];

/**
 * The earliest row time of each marked session, across every collection — the marked
 * session's compromise START. Only the run's own data can be seen, so a session created
 * before the run window starts at its first row INSIDE the window, and nothing earlier.
 * @returns Map<sessionId, isoTime>
 */
export function markedSessionStarts(results, marks) {
  const out = new Map();
  if (!results || !(marks instanceof Map) || marks.size === 0) return out;
  for (const key of SESSION_MARK_SOURCES) {
    for (const evt of results[key] || []) {
      const sid = evt?.SessionId || evt?.AADSessionId;
      if (!sid || !marks.has(sid)) continue;
      const ts = evt.EventTimeUTC;
      if (!ts) continue;
      const cur = out.get(sid);
      if (!cur || ts < cur) out.set(sid, ts);
    }
  }
  return out;
}
